import { useMemo, useState, type ReactNode } from 'react';
import {
  areaFeatureState,
  AREA_FEATURES,
  featureLiveInArea,
  resolveKinds,
  type AreaCatalog,
  type AreaFeatureKey,
} from '@domain/entities/AreaCatalog';
import type { IncapacityKind } from '@domain/entities/Incapacity';
import type { PermissionKind } from '@domain/entities/PermissionRequest';
import { FEATURE_CATALOG } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import type { AreaId } from '@shared/brand';
import { Banner, Button, Card, Chip, cx } from '../../design-system';
import { reasonFor } from '../../format';
import { useAreaName } from '../../hooks/useAreaName';
import { useAreaFocus } from '../../providers/AreaFocusProvider';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './AreaCatalogsScreen.module.css';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Incapacidades y permisos, por área.
 *
 * Los dos catálogos eran de la empresa entera: una lista que editaba el
 * superusuario y que las ocho áreas ofrecían igual. No es como se trabaja aquí —
 * Customer Kare no reparte home office como Tecnología—, y quien sabe eso dirige
 * el área, no la plataforma.
 *
 * **Esta pantalla enseña de dónde viene cada tipo**, que es la pregunta que trae
 * a alguien aquí: no «qué tipos hay» sino «por qué en mi área sí y en la otra
 * no». Un tipo puede venir de la empresa tal cual, venir de la empresa y estar
 * corregido aquí, o ser de esta área y no existir en ninguna otra. Sin esa
 * distinción, apagar algo parece un cambio de empresa y no lo es.
 *
 * **Se guardan diferencias, no copias.** Lo que no se toca no se guarda, así que
 * un tipo que la empresa dé de alta mañana aparece solo. Ver `AreaCatalog`.
 */

type Tab = 'permisos' | 'incapacidades';

export const AreaCatalogsScreen = (): ReactNode => {
  const session = useSession();
  const focus = useAreaFocus();
  const config = useRuntimeConfig();
  const toast = useToast();
  const areaName = useAreaName();

  const [tab, setTab] = useState<Tab>('permisos');
  const [busy, setBusy] = useState(false);
  const [nuevo, setNuevo] = useState('');

  /* El área que se está editando: la suya, o la que el superusuario haya puesto
     en foco. El mismo selector que gobierna las demás pantallas de gestión. */
  const areaId: AreaId = focus?.areaId ?? session.areaId;
  const propio = config.areaCatalogs[areaId];

  const permisos = useMemo(
    () => resolveKinds(config.catalogs.permissionKinds, propio?.permissionKinds ?? []),
    [config.catalogs.permissionKinds, propio],
  );
  const incapacidades = useMemo(
    () => resolveKinds(config.catalogs.incapacityKinds, propio?.incapacityKinds ?? []),
    [config.catalogs.incapacityKinds, propio],
  );

  const idsGlobales = useMemo(
    () => ({
      permisos: new Set(config.catalogs.permissionKinds.map((k) => k.id)),
      incapacidades: new Set(config.catalogs.incapacityKinds.map((k) => k.id)),
    }),
    [config.catalogs.permissionKinds, config.catalogs.incapacityKinds],
  );

  const tocados = useMemo(
    () => ({
      permisos: new Set((propio?.permissionKinds ?? []).map((k) => k.id)),
      incapacidades: new Set((propio?.incapacityKinds ?? []).map((k) => k.id)),
    }),
    [propio],
  );

  /**
   * Guarda la lista de diferencias entera.
   *
   * Se manda todo y no un parche porque lo que se edita **es** la lista: mandar
   * sólo lo cambiado obligaría al servidor a deducir qué se quitó, y ahí es donde
   * se pierden los borrados.
   */
  const guardar = async (
    siguiente: {
      readonly permissionKinds?: readonly Partial<PermissionKind>[];
      readonly incapacityKinds?: readonly Partial<IncapacityKind>[];
      readonly features?: AreaCatalog['features'];
    },
    mensaje: string,
  ): Promise<void> => {
    setBusy(true);
    const result = await getServices().organization.saveAreaCatalog({
      areaId,
      permissionKinds: siguiente.permissionKinds ?? propio?.permissionKinds ?? [],
      incapacityKinds: siguiente.incapacityKinds ?? propio?.incapacityKinds ?? [],
      features: siguiente.features ?? propio?.features ?? {},
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudo guardar.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(mensaje);
  };

  /** Enciende o apaga un tipo en esta área, dejando el resto de su corrección. */
  const alternar = async (id: string, next: boolean): Promise<void> => {
    const clave = tab === 'permisos' ? 'permissionKinds' : 'incapacityKinds';
    const actual = (propio?.[clave] ?? []) as readonly Partial<PermissionKind>[];
    const existe = actual.some((k) => k.id === id);
    const siguiente = existe
      ? actual.map((k) => (k.id === id ? { ...k, enabled: next } : k))
      : [...actual, { id, enabled: next }];

    await guardar(
      { [clave]: siguiente },
      next ? `${id} se ofrece en ${areaName}.` : `${id} deja de ofrecerse en ${areaName}.`,
    );
  };

  /**
   * Devuelve un tipo a como lo tiene la empresa.
   *
   * Sólo aparece sobre los que vienen del catálogo global: sobre uno propio del
   * área no hay nada a lo que volver, y ahí el botón es «Eliminar».
   */
  const restaurar = async (id: string): Promise<void> => {
    const clave = tab === 'permisos' ? 'permissionKinds' : 'incapacityKinds';
    const actual = (propio?.[clave] ?? []) as readonly Partial<PermissionKind>[];
    await guardar({ [clave]: actual.filter((k) => k.id !== id) }, `${id} vuelve a lo de la empresa.`);
  };

  const crear = async (): Promise<void> => {
    const nombre = nuevo.trim();
    if (nombre.length === 0) return;

    const clave = tab === 'permisos' ? 'permissionKinds' : 'incapacityKinds';
    const lista = tab === 'permisos' ? permisos : incapacidades;
    if (lista.some((k) => k.id.toLowerCase() === nombre.toLowerCase())) {
      toast.bad(`${nombre} ya existe en esta lista.`);
      return;
    }

    const actual = (propio?.[clave] ?? []) as readonly Partial<PermissionKind>[];
    /* Un tipo propio nace encendido y por días: es lo que se espera de algo que
       alguien acaba de dar de alta, y las banderas finas se ajustan después. */
    const entrada =
      tab === 'permisos'
        ? { id: nombre, enabled: true, note: '', icon: '·', measuredInHours: false }
        : { id: nombre, enabled: true, note: '', subsidy: '—' };

    await guardar({ [clave]: [...actual, entrada] }, `${nombre} queda disponible en ${areaName}.`);
    setNuevo('');
  };

  /**
   * Enciende o apaga un módulo entero para esta área.
   *
   * No toca lo global: escribe la diferencia de esta área, que es lo que hace
   * posible lo que trajo la función —un área con permisos cuando la empresa
   * todavía no los reparte—.
   */
  const alternarModulo = async (key: AreaFeatureKey, next: boolean): Promise<void> => {
    const etiqueta = FEATURE_CATALOG.find((f) => f.key === key)?.label ?? key;
    await guardar(
      { features: { ...(propio?.features ?? {}), [key]: next } },
      next ? `${etiqueta} se activa en ${areaName}.` : `${etiqueta} se apaga en ${areaName}.`,
    );
  };

  /** Devuelve un módulo a lo que diga la empresa, hoy y cuando la empresa cambie. */
  const heredarModulo = async (key: AreaFeatureKey): Promise<void> => {
    const etiqueta = FEATURE_CATALOG.find((f) => f.key === key)?.label ?? key;
    const resto = { ...(propio?.features ?? {}) };
    delete resto[key];
    await guardar({ features: resto }, `${etiqueta} vuelve a lo de la empresa.`);
  };

  const eliminar = async (id: string): Promise<void> => {
    const clave = tab === 'permisos' ? 'permissionKinds' : 'incapacityKinds';
    const actual = (propio?.[clave] ?? []) as readonly Partial<PermissionKind>[];
    await guardar({ [clave]: actual.filter((k) => k.id !== id) }, `${id} ya no existe en ${areaName}.`);
  };

  const lista: readonly { id: string; note: string; enabled: boolean }[] =
    tab === 'permisos'
      ? permisos.map((k) => ({ id: k.id, note: k.note, enabled: k.enabled }))
      : incapacidades.map((k) => ({ id: k.id, note: k.note, enabled: k.enabled }));

  const globales = tab === 'permisos' ? idsGlobales.permisos : idsGlobales.incapacidades;
  const conCambio = tab === 'permisos' ? tocados.permisos : tocados.incapacidades;

  return (
    <div className="flow">
      <PageTitle className={styles.title}>Incapacidades y permisos</PageTitle>
      <p className={styles.lead}>
        Qué se puede pedir en <strong>{areaName}</strong>. Cada área lo decide por su cuenta:
        lo que apagues aquí sigue disponible en las demás.
      </p>

      {/*
        Los módulos van antes que los tipos, y en su propia tarjeta, porque la
        pregunta es anterior: de nada sirve elegir qué permisos se ofrecen en un
        área donde el módulo de permisos está apagado.
      */}
      <Card>
        <h2 className={styles.sectionTitle}>Los módulos en esta área</h2>
        <Banner tone="info">
          Un módulo apagado en la empresa <strong>no está prohibido</strong>: puedes
          encenderlo aquí y sólo <strong>{areaName}</strong> lo tendrá. «De la empresa»
          significa que este módulo seguirá a la configuración global cuando cambie.
        </Banner>

        <ul className={styles.rows}>
          {AREA_FEATURES.map((key) => {
            const descriptor = FEATURE_CATALOG.find((f) => f.key === key);
            const estado = areaFeatureState(propio, key);
            const vivo = featureLiveInArea(config.features, propio, key);
            const global = config.features[key] === true;

            return (
              <li key={key} className={cx(styles.row, !vivo && styles.rowOff)}>
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{descriptor?.label ?? key}</span>
                  <span className={styles.rowNote}>
                    {descriptor?.note ?? '—'} · En la empresa está{' '}
                    {global ? 'encendido' : 'apagado'}
                  </span>
                </span>

                <Chip
                  background={
                    estado === 'hereda'
                      ? 'var(--color-surface-subtle)'
                      : 'var(--color-warning-bg)'
                  }
                  foreground={
                    estado === 'hereda'
                      ? 'var(--color-text-tertiary)'
                      : 'var(--color-warning-ink)'
                  }
                >
                  {estado === 'hereda'
                    ? 'De la empresa'
                    : estado === 'encendido'
                      ? 'Encendido aquí'
                      : 'Apagado aquí'}
                </Chip>

                <Toggle
                  on={vivo}
                  disabled={busy}
                  label={`${vivo ? 'Apagar' : 'Encender'} ${descriptor?.label ?? key} en ${areaName}`}
                  onToggle={() => {
                    void alternarModulo(key, !vivo);
                  }}
                />

                {estado === 'hereda' ? (
                  <span className={styles.rowSpacer} />
                ) : (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      void heredarModulo(key);
                    }}
                  >
                    Volver a lo de la empresa
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      <Card>
        <div className={styles.head}>
          <div className={styles.tabs} role="tablist" aria-label="Tipo de ausencia">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'permisos'}
              className={cx(styles.tab, tab === 'permisos' && styles.tabActive)}
              onClick={() => {
                setTab('permisos');
              }}
            >
              Permisos
              <span className={styles.tabBadge}>{permisos.filter((k) => k.enabled).length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'incapacidades'}
              className={cx(styles.tab, tab === 'incapacidades' && styles.tabActive)}
              onClick={() => {
                setTab('incapacidades');
              }}
            >
              Incapacidades
              <span className={styles.tabBadge}>
                {incapacidades.filter((k) => k.enabled).length}
              </span>
            </button>
          </div>
        </div>

        {/*
          Lo que hace falta saber antes de tocar nada: apagar aquí no borra nada
          de la empresa, y lo ya pedido no se deshace.
        */}
        <Banner tone="info">
          Apagar un tipo lo quita del formulario de <strong>{areaName}</strong> de aquí en
          adelante. Lo que ya se pidió con él no cambia, y las demás áreas siguen igual.
        </Banner>

        <ul className={styles.rows}>
          {lista.map((kind) => {
            const esGlobal = globales.has(kind.id);
            const tocado = conCambio.has(kind.id);

            return (
              <li key={kind.id} className={cx(styles.row, !kind.enabled && styles.rowOff)}>
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{kind.id}</span>
                  <span className={styles.rowNote}>{kind.note.length > 0 ? kind.note : '—'}</span>
                </span>

                {/* De dónde viene. Es la columna que contesta «¿por qué aquí sí?». */}
                <Chip
                  background={
                    !esGlobal
                      ? 'var(--color-violet-bg)'
                      : tocado
                        ? 'var(--color-warning-bg)'
                        : 'var(--color-surface-subtle)'
                  }
                  foreground={
                    !esGlobal
                      ? 'var(--color-violet-ink)'
                      : tocado
                        ? 'var(--color-warning-ink)'
                        : 'var(--color-text-tertiary)'
                  }
                >
                  {!esGlobal ? 'De esta área' : tocado ? 'Cambiado aquí' : 'De la empresa'}
                </Chip>

                <Toggle
                  on={kind.enabled}
                  disabled={busy}
                  label={`${kind.enabled ? 'Apagar' : 'Encender'} ${kind.id} en ${areaName}`}
                  onToggle={() => {
                    void alternar(kind.id, !kind.enabled);
                  }}
                />

                {esGlobal ? (
                  tocado && (
                    <button
                      type="button"
                      className={styles.rowAction}
                      disabled={busy}
                      onClick={() => {
                        void restaurar(kind.id);
                      }}
                    >
                      Volver a lo de la empresa
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    className={cx(styles.rowAction, styles.rowRemove)}
                    disabled={busy}
                    onClick={() => {
                      void eliminar(kind.id);
                    }}
                  >
                    Eliminar
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.newRow}>
          <label className={styles.newField}>
            <span className={styles.newLabel}>
              {tab === 'permisos' ? 'Nuevo permiso de esta área' : 'Nueva incapacidad de esta área'}
            </span>
            <input
              type="text"
              className={styles.newInput}
              placeholder={tab === 'permisos' ? 'Ej. Día de campo' : 'Ej. Reposo indicado'}
              value={nuevo}
              onChange={(event) => {
                setNuevo(event.target.value);
              }}
            />
          </label>
          <Button
            variant="primary"
            disabled={busy || nuevo.trim().length === 0}
            onClick={() => {
              void crear();
            }}
          >
            Agregar
          </Button>
        </div>
        <p className={styles.newNote}>
          Sólo se ofrece en {areaName}. Las demás áreas no lo ven, y los reportes de empresa lo
          cuentan aparte.
        </p>
      </Card>
    </div>
  );
};

/**
 * El interruptor, igual que el de las áreas.
 *
 * Copiado y no exportado desde el sistema de diseño porque allí no está: vive
 * dentro de `SystemScreens` desde que se transcribió el prototipo. Sacarlo a la
 * biblioteca es una limpieza que toca dos pantallas más y no es ésta.
 */
const Toggle = ({
  on,
  label,
  disabled = false,
  onToggle,
}: {
  readonly on: boolean;
  readonly label: string;
  readonly disabled?: boolean;
  readonly onToggle: () => void;
}): ReactNode => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    className={cx(styles.toggle, on && styles.toggleOn)}
    onClick={onToggle}
  >
    <span className={cx(styles.toggleKnob, on && styles.toggleKnobOn)} />
  </button>
);
